import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState } from "react";
// material-ui
import { useTheme, alpha } from "@mui/material/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";

import { useSupabaseAuth } from "contexts/SupabaseAuthContext";

import { markNotificationAsRead } from "store/NotificationSlice";

const listSx = {
  width: "100%",
  py: 0,
  borderRadius: "10px",
};

const ListItemWrapper = ({ children }) => {
  const theme = useTheme();
  const baseBackground =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.04)
      : "transparent";
  const hoverBackground =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.2)
      : theme.palette.primary.light;

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        cursor: "pointer",
        bgcolor: baseBackground,
        transition: "background-color .2s ease",
        "&:hover": {
          bgcolor: hoverBackground,
        },
      }}
    >
      {children}
    </Box>
  );
};

ListItemWrapper.propTypes = {
  children: PropTypes.node,
};

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

function formatRelativeTime(dateString) {
  if (!dateString) return "Just now";
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
  if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  const days = Math.floor(diffInMinutes / 1440);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

const NotificationList = () => {
  const dispatch = useDispatch();
  const { userId } = useSupabaseAuth();
  const notices = useSelector((state) => state.notificationReducer.notices);
  const loading = useSelector((state) => state.notificationReducer.loading);

  const [fadingIds, setFadingIds] = useState(() => new Set());
  const fadeTimeoutsRef = useRef({});

  // Keep "read" items only while they are actively fading out.
  const visibleNotices = (notices || []).filter((notice) => {
    if (!notice) return false;
    if (notice.status !== "read") return true;
    return notice.id != null && fadingIds.has(notice.id);
  });

  const handleMarkAsRead = async (id) => {
    if (!id || !userId) return;

    // Start fade immediately. Once the thunk updates the row to "read",
    // `visibleNotices` will drop it after the fade delay.
    setFadingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    const FADE_MS = 250;
    try {
      await dispatch(markNotificationAsRead({ id, userId })).unwrap();

      if (fadeTimeoutsRef.current[id]) {
        clearTimeout(fadeTimeoutsRef.current[id]);
      }

      fadeTimeoutsRef.current[id] = setTimeout(() => {
        setFadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        delete fadeTimeoutsRef.current[id];
      }, FADE_MS);
    } catch (err) {
      // If the update fails, revert the fade state.
      setFadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      console.error("handleMarkAsRead failed:", err);
    }
  };

  if (loading) {
    return (
      <List sx={listSx}>
        {[1, 2, 3].map((index) => (
          <Box key={index}>
            <ListItemWrapper>
              <ListItem alignItems="center">
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                />
                <ListItemSecondaryAction>
                  <Skeleton variant="text" width="50px" />
                </ListItemSecondaryAction>
              </ListItem>
              <Grid container direction="column" className="list-container">
                <Grid sx={{ pb: 2 }} size={12}>
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="70%" />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" width="80px" height="24px" />
                </Grid>
              </Grid>
            </ListItemWrapper>
            {index < 3 && <Divider />}
          </Box>
        ))}
      </List>
    );
  }

  if (!visibleNotices?.length) {
    return (
      <List sx={listSx}>
        <ListItemWrapper>
          <ListItem alignItems="center">
            <ListItemText
              primary={
                <Typography variant="subtitle1" align="center">
                  No New Notifications
                </Typography>
              }
            />
          </ListItem>
          <Box sx={{ pt: 0, pb: 2 }}>
            <Typography variant="body2" align="center" color="textSecondary">
              You're all caught up! New notifications will appear here.
            </Typography>
          </Box>
        </ListItemWrapper>
      </List>
    );
  }

  return (
    <List
      sx={{
        ...listSx,
        "& .MuiDivider-root": { my: 0 },
        "& .list-container": { pl: 0 },
      }}
    >
      {visibleNotices.map((notice, index) => {
        if (!notice) return null;
        const isFading = notice.id != null && fadingIds.has(notice.id);
        return (
          <Box
            key={notice.id ?? index}
            sx={{
              opacity: isFading ? 0 : 1,
              transform: isFading ? "translateY(-4px)" : "translateY(0)",
              transition: "opacity 250ms ease, transform 250ms ease",
              pointerEvents: isFading ? "none" : "auto",
            }}
          >
            <ListItemWrapper>
              {notice.status === "read" ? (
                <ListItem>
                  <ListItemText
                    primary={<Typography variant="body1"
                      sx={{ maxWidth: 380 }}
                    >
                      {notice.description || "No message available"}
                    </Typography>}
                    secondary={<Typography variant="caption" color="textSecondary">
                      {formatRelativeTime(notice.created_at)} ({notice.category || "Unknown"})
                    </Typography>}
                  />
                </ListItem>
              ) : (
                <Tooltip title="Click to Mark Notice As Read" arrow>
                  <ListItem onClick={() => handleMarkAsRead(notice.id)}>
                    <ListItemText
                      primary={<Typography variant="body1"

                      >
                        {notice.description || "No message available"}
                      </Typography>}
                      secondary={<Typography variant="caption" color="textSecondary">
                        {formatRelativeTime(notice.created_at)} ({notice.category || "Unknown"})
                      </Typography>}
                    />

                    {/* <IconButton
                      sx={{ mr: 1 }}
                    >
                      <MarkunreadIcon />
                    </IconButton> */}




                  </ListItem>
                </Tooltip>
              )}



            </ListItemWrapper>
            {index < visibleNotices.length - 1 && <Divider />}
          </Box>
        );
      })}
    </List >
  );
};

export default NotificationList;
